import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Stack, Radio, Card, CardContent, CardHeader } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { Edit as EditIcon } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import { useDTBLibraryContext } from '../../context';
import DeleteIcon from '@mui/icons-material/Delete';
import parse, { DOMNode } from 'html-react-parser';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import DTBContentEditor from './DTBContentEditor';
import ContentConversionHelper from 'views/App/Portal/Policy/DraftManuals/helpers/contentConversionHelper';

const DTBDetailQuizTab: React.FC = () => {
  const { editedData, setEditedData, dtbData } = useDTBLibraryContext();
  const [questions, setQuestions] = useState<
    Array<{
      id: string;
      questionTextEditable: string;
      questionTextDisplay: string;
      choices: { id: string; value: string }[];
      correctChoiceId: string;
      allowOther: boolean;
    }>
  >([]);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [tempContent, setTempContent] = useState<string>('');

  useEffect(() => {
    if (editedData) {
      if (!editedData.questions || editedData.questions.length === 0) {
        const defaultQuestion = {
          id: uuidv4(),
          questionTextEditable: '',
          questionTextDisplay: '',
          choices: [
            { id: uuidv4(), value: 'Option' },
            { id: uuidv4(), value: 'Option' },
          ],
          correctChoiceId: '',
          allowOther: false,
        };

        setEditedData((prev: any) => ({
          ...prev,
          questions: [defaultQuestion],
        }));
      } else {
        const formattedQuestions = editedData.questions.map((q: any) => ({
          id: q.id,
          questionTextEditable: q.questionEditable || q.question,
          questionTextDisplay: q.question || q.questionEditable,
          choices: q.choices ? q.choices.filter((choice: any) => choice.value !== 'Other') : [],
          correctChoiceId: q.correctChoiceId || '',
          allowOther: q.choices && q.choices.find((c: any) => c.value === 'Other'),
        }));
        setQuestions(formattedQuestions);
      }
    }
  }, [editedData]);

  const handleQuestionChange = (questionId: string, newText: string) => {
    const convertedContent = ContentConversionHelper.contentForRender(newText);

    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId ? { ...q, questionTextEditable: newText, questionTextDisplay: convertedContent } : q
      )
    );
    setEditedData((prev: any) => {
      if (!prev) return null;
      const updatedQuestions = prev.questions.map((q: any) =>
        q.id === questionId ? { ...q, questionEditable: newText, question: convertedContent } : q
      );
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleChoiceChange = (questionId: string, index: number, newValue: string) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          const updatedChoices = [...q.choices];
          updatedChoices[index].value = newValue;
          return { ...q, choices: updatedChoices };
        }
        return q;
      })
    );
    setEditedData((prev: any) => {
      if (!prev) return null;
      const updatedQuestions = prev.questions.map((q: any) => {
        if (q.id === questionId) {
          const updatedChoices = [...q.choices];
          updatedChoices[index].value = newValue;
          return { ...q, choices: updatedChoices };
        }
        return q;
      });
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleAddChoice = (questionId: string) => {
    const newChoice = { id: uuidv4(), value: 'Option' };
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            choices: [...q.choices, newChoice],
          };
        }
        return q;
      })
    );
    setEditedData((prev: any) => {
      if (!prev) return null;
      const updatedQuestions = prev.questions.map((q: any) => {
        if (q.id === questionId) {
          return {
            ...q,
            choices: [...q.choices, newChoice],
          };
        }
        return q;
      });
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleRemoveChoice = (questionId: string, index: number) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          const removedChoice = q.choices[index];
          const updatedChoices = q.choices.filter((_: any, i: number) => i !== index);

          if (updatedChoices.length < 2) {
            updatedChoices.push({ id: uuidv4(), value: 'Option' });
          }

          return {
            ...q,
            choices: updatedChoices,
            correctChoiceId: removedChoice.id === q.correctChoiceId ? '' : q.correctChoiceId,
          };
        }
        return q;
      })
    );
    setEditedData((prev: any) => {
      if (!prev) return null;
      const updatedQuestions = prev.questions.map((q: any) => {
        if (q.id === questionId) {
          const removedChoice = q.choices[index];
          const updatedChoices = q.choices.filter((_: any, i: number) => i !== index);

          if (updatedChoices.length < 2) {
            updatedChoices.push({ id: uuidv4(), value: 'Option' });
          }

          return {
            ...q,
            choices: updatedChoices,
            correctChoiceId: removedChoice.id === q.correctChoiceId ? '' : q.correctChoiceId,
          };
        }
        return q;
      });
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleCorrectChoiceChange = (questionId: string, choiceId: string) => {
    setQuestions((prev) => prev.map((q) => (q.id === questionId ? { ...q, correctChoiceId: choiceId } : q)));
    setEditedData((prev: any) => {
      if (!prev) return null;
      const updatedQuestions = prev.questions.map((q: any) =>
        q.id === questionId ? { ...q, correctChoiceId: choiceId } : q
      );
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleEditClick = (questionId: string | null) => {
    if (questionId === null) {
      setEditingQuestionId(null);
      setTempContent('');
      return;
    }

    const question = questions.find((q) => q.id === questionId);
    if (question) {
      setTempContent(question.questionTextEditable);
      setEditingQuestionId(questionId);
    }
  };

  if (!editedData) return <Typography>Loading...</Typography>;

  return (
    <>
      {questions.map((question) => (
        <Card
          key={question.id}
          className='lex-card'
          sx={{ mb: 4 }}
        >
          <CardHeader
            title={t('dtbs.library.detail.quiz.title')}
            action={
              editingQuestionId === question.id ? (
                <Button
                  variant='outlined'
                  color='primary'
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(null);
                  }}
                >
                  Done
                </Button>
              ) : (
                <Button
                  variant='outlined'
                  color='primary'
                  size='small'
                  startIcon={<EditIcon />}
                  className='xs icon-only'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(question.id);
                  }}
                />
              )
            }
          />
          <CardContent>
            <Stack spacing={4}>
              <Box>
                {editingQuestionId === question.id ? (
                  <Box>
                    <DTBContentEditor
                      id={`quiz_question_${question.id}`}
                      contentHTML={tempContent}
                      onSave={(content: string) => {
                        setTempContent(content);
                        handleQuestionChange(question.id, content);
                      }}
                      isEditable
                      manualId={dtbData?.manual?.id}
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box
                      sx={{
                        padding: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        width: '100%',
                      }}
                    >
                      {parse(question.questionTextDisplay || 'Title question', {
                        replace(node: DOMNode, ind: number) {
                          return PolicyContentHelper.handleContent(node, ind);
                        },
                      })}
                    </Box>
                  </Box>
                )}
                <Card
                  className='lex-form-card'
                  sx={{ mt: 2 }}
                >
                  <CardContent>
                    <Box className='lex-form-card-header'>
                      <Typography variant='subtitle2'>{t('dtbs.library.detail.quiz.choices')}</Typography>
                    </Box>
                    <Box className='lex-form-card-content'>
                      <Stack spacing={4}>
                        {question.choices.map((choice, index) => (
                          <Box
                            key={choice.id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'auto 1fr auto',
                              gap: 2,
                              alignItems: 'center',
                            }}
                          >
                            <Radio
                              checked={question.correctChoiceId === choice.id}
                              onChange={() => handleCorrectChoiceChange(question.id, choice.id)}
                            />
                            <TextField
                              label={false}
                              value={choice.value}
                              onChange={(e) => handleChoiceChange(question.id, index, e.target.value)}
                              fullWidth
                              sx={{
                                '&.MuiFormControl-root': {
                                  paddingTop: '0px !important',
                                },
                              }}
                            />
                            {editingQuestionId === question.id && (
                              <Button
                                variant='text'
                                color='primary'
                                onClick={() => handleRemoveChoice(question.id, index)}
                              >
                                <DeleteIcon />
                              </Button>
                            )}
                          </Box>
                        ))}
                        {question.allowOther && (
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'auto 1fr',
                              gap: 2,
                              alignItems: 'center',
                              opacity: 0.6,
                            }}
                          >
                            <Radio
                              checked={question.correctChoiceId === 'other'}
                              onChange={() => handleCorrectChoiceChange(question.id, 'other')}
                            />
                            <TextField
                              label='Other'
                              value='Other'
                              disabled
                              fullWidth
                            />
                          </Box>
                        )}
                        {editingQuestionId === question.id && (
                          <Stack
                            direction='row'
                            spacing={2}
                            sx={{ mb: 3 }}
                          >
                            <Button
                              variant='text'
                              color='primary'
                              size='small'
                              onClick={() => handleAddChoice(question.id)}
                            >
                              {t('dtbs.library.detail.quiz.addOption')}
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </>
  );
};

export default DTBDetailQuizTab;
